import React from 'react';
import { Video, Clock, Link, Activity } from 'lucide-react';
import { SectionCard, MetricCard, TabLoading, TabError, TabEmpty, StatusBadge, getGenericStatusVariant } from './shared';
import { useUserRecordings } from '../../hooks/useUserTabs';
import { formatNumber, formatDateTime } from '../../utils/helpers';

interface RecordingsTabProps {
  userId: number;
}

function formatRecordingDuration(seconds: number | null): string {
  if (seconds == null) return 'N/A';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
}

function normalizeArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'items' in data && Array.isArray((data as any).items)) {
    return (data as any).items;
  }
  return [];
}

const RecordingsTab: React.FC<RecordingsTabProps> = ({ userId }) => {
  const { data: rawData, loading, error, refetch } = useUserRecordings(userId, true);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!rawData) return <TabEmpty message="No recordings data available" />;

  const data = normalizeArray<import('../../types/userTabs').SessionRecordingItem>(rawData);

  if (data.length === 0) {
    return <TabEmpty message="No session recordings found" icon={Video} />;
  }

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-1 gap-4">
        <MetricCard
          label="Total Recordings"
          value={formatNumber(data.length)}
          icon={Video}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
        />
      </div>

      {/* Recordings List */}
      <SectionCard
        title="Session Recordings"
        icon={Video}
        subtitle={`${data.length} recording${data.length !== 1 ? 's' : ''}`}
      >
        <div className="space-y-3">
          {data.map((recording) => (
            <div
              key={recording.id}
              className="rounded-lg border border-border/50 bg-muted/5 p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-violet-500/10">
                    <Video className="h-3.5 w-3.5 text-violet-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Recording #{recording.id}
                    </p>
                  </div>
                </div>
                <StatusBadge
                  status={recording.status}
                  variant={getGenericStatusVariant(recording.status)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3 ml-9">
                {/* Duration */}
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Activity className="h-3 w-3" />
                  <span className="text-[11px]">
                    Duration: {formatRecordingDuration(recording.duration)}
                  </span>
                </div>

                {/* Created Date */}
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span className="text-[11px]">{formatDateTime(recording.createdAt)}</span>
                </div>
              </div>

              {/* URL */}
              {recording.url && (
                <div className="flex items-center gap-1.5 text-muted-foreground mt-2 ml-9">
                  <Link className="h-3 w-3 flex-shrink-0" />
                  <span className="text-[11px] font-mono truncate">
                    {recording.url.length > 60
                      ? `${recording.url.slice(0, 60)}...`
                      : recording.url}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

export default RecordingsTab;
